import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import { api } from '../../api/client';
import { PasswordFieldToggle } from '../../components/PasswordFieldToggle';
import { PhoneWithCountryInput } from '../../components/PhoneWithCountryInput/PhoneWithCountryInput';
import '../Login/login.css';

const ROLE_USER = 3;

const Signup = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
  });
  const [errorList, setErrorList] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  const toErrorList = (err) => {
    const bag = err?.data?.errors;
    if (bag && typeof bag === 'object') {
      const messages = Object.values(bag).flat().filter(Boolean);
      return messages.length > 0 ? messages : [err?.message || 'Could not create account.'];
    }
    return [err?.message || 'Could not create account. Please check your details.'];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errorList.length > 0) setErrorList([]);
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorList([]);
    setFieldErrors({});

    if (form.password !== form.password_confirmation) {
      const msg = 'Password confirmation does not match.';
      setFieldErrors({ password_confirmation: msg });
      setErrorList([msg]);
      setSubmitting(false);
      return;
    }

    const phone = String(form.phone_number || '').trim();
    if (!phone) {
      const msg = 'Please enter your phone number.';
      setFieldErrors({ phone_number: msg });
      setErrorList([msg]);
      setSubmitting(false);
      return;
    }
    if (!isValidPhoneNumber(phone)) {
      const msg =
        'Choose your country, then enter your mobile number without the leading 0 (e.g. 916 001 0451 for Nigeria).';
      setFieldErrors({ phone_number: msg });
      setErrorList([msg]);
      setSubmitting(false);
      return;
    }

    try {
      await api.post('/auth/register', {
        ...form,
        role_id: ROLE_USER,
      }, null);

      setRegistered(true);
      setErrorList([]);
      setFieldErrors({});
      // Do not store a token until email is verified (API does not return one).
    } catch (err) {
      setErrorList(toErrorList(err));
      if (err?.data?.errors && typeof err.data.errors === 'object') {
        const perField = {};
        Object.entries(err.data.errors).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages.length > 0) {
            perField[key] = messages[0];
          }
        });
        setFieldErrors(perField);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (registered) {
    return (
      <div className="login">
        <SEO title="Check your email" description="Verify your Luxistt account." path="/signup" noindex />
        <Navbar />
        <div className="login-wrap">
          <div className="login-card">
            <h1 className="login-title">Check your email</h1>
            <p className="login-subtitle">
              Your account has been created. We sent a message to <strong>{form.email}</strong> with a link to verify your email.
              After you verify, you can sign in and use your dashboard, bookings, and more.
            </p>
            <div className="login-success" role="status">
              Didn&apos;t get the email? Check your spam folder, or try signing up again with the same details after a few minutes.
            </div>
            <Link to="/login" className="login-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Go to sign in
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="login">
      <SEO title="Sign Up" description="Create a Luxistt account to book short stays and manage reservations in Nigeria." path="/signup" noindex />
      <Navbar />
      <div className="login-wrap">
        <div className="login-card">
          <h1 className="login-title">Create account</h1>
          <p className="login-subtitle">Create an account to browse apartments on Luxistt, book your stay, and manage reservations — all in one place.</p>
          {errorList.length > 0 && (
            <div className="login-error" role="alert">
              <ul className="login-error-list">
                {errorList.map((msg, idx) => <li key={`${msg}-${idx}`}>{msg}</li>)}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form-fields">
            <label className="login-label" htmlFor="signup-name">Full Name</label>
            <input
              id="signup-name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              className={`login-input${fieldErrors.name ? ' login-input-error' : ''}`}
              required
              placeholder="John Doe"
            />
            {fieldErrors.name && <small className="login-field-error">{fieldErrors.name}</small>}

            <label className="login-label" htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className={`login-input${fieldErrors.email ? ' login-input-error' : ''}`}
              required
              placeholder="you@example.com"
            />
            {fieldErrors.email && <small className="login-field-error">{fieldErrors.email}</small>}

            <label className="login-label" htmlFor="signup-phone">Phone Number</label>
            <p id="signup-phone-hint" className="login-phone-hint">
              Pick your country (flag), then type your number without the leading 0 — for example 916 001 0451 when Nigeria (+234) is selected.
            </p>
            <PhoneWithCountryInput
              id="signup-phone"
              name="phone_number"
              value={form.phone_number}
              onChange={(v) => {
                setForm((prev) => ({ ...prev, phone_number: v }));
                if (errorList.length > 0) setErrorList([]);
                if (fieldErrors.phone_number) {
                  setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next.phone_number;
                    return next;
                  });
                }
              }}
              variant="login"
              hasError={!!fieldErrors.phone_number}
              aria-invalid={fieldErrors.phone_number ? true : undefined}
              aria-describedby={
                fieldErrors.phone_number ? 'signup-err-phone signup-phone-hint' : 'signup-phone-hint'
              }
            />
            {fieldErrors.phone_number && (
              <small id="signup-err-phone" className="login-field-error">
                {fieldErrors.phone_number}
              </small>
            )}

            <PasswordFieldToggle
              id="signup-password"
              name="password"
              label="Password"
              value={form.password}
              onChange={handleChange}
              inputClassName={`login-input${fieldErrors.password ? ' login-input-error' : ''}`}
              required
              minLength={8}
              placeholder="At least 8 characters"
              errorText={fieldErrors.password}
            />

            <PasswordFieldToggle
              id="signup-password-confirmation"
              name="password_confirmation"
              label="Confirm Password"
              value={form.password_confirmation}
              onChange={handleChange}
              inputClassName={`login-input${fieldErrors.password_confirmation ? ' login-input-error' : ''}`}
              required
              minLength={8}
              placeholder="Repeat password"
              errorText={fieldErrors.password_confirmation}
            />

            <button type="submit" disabled={submitting} className="login-btn">
              {submitting ? (
                <span className="login-btn-spinner">
                  <span className="login-spinner" aria-hidden />
                  <span>Creating account…</span>
                </span>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          <div className="login-meta-row">
            <span>Already have an account?</span>
            <Link to="/login" className="login-meta-link">Login</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Signup;
